import { useEffect, useRef } from 'react';
import { isWithinKCRegion } from '../components/ExploreProvider';

/**
 * Location management hook
 * Handles all location-related logic and state including dynamic tracking
 */

export function useExploreLocation(state, dispatch, {
  setMapCenter,
  setMapZoom,
  onPermissionChange
} = {}) {
  const watchIdRef = useRef(null);
  const permissionCheckStartedRef = useRef(false);
  const modalDismissedRef = useRef(false);
  const previousPermissionRef = useRef(state.locationPermission);

  // Set location permission
  const setLocationPermission = permission => {
    const previousPermission = previousPermissionRef.current;

    // Track when modal is dismissed
    if (permission === 'dismissed') {
      modalDismissedRef.current = true;
    }
    dispatch({
      type: 'SET_LOCATION_PERMISSION',
      payload: permission
    });

    // Notify parent if permission changed from denied/dismissed to prompt
    // This allows auto-request to fire again when user re-enables in browser settings
    if ((previousPermission === 'denied' || previousPermission === 'dismissed') && permission === 'prompt') {
      onPermissionChange?.(permission, previousPermission);
    }
    previousPermissionRef.current = permission;
  };

  // Clear dismissal flag (called when user clicks location button)
  const clearDismissal = () => {
    modalDismissedRef.current = false;
  };

  // Check persisted permission state on mount
  // This syncs with browser's stored permission before any auto-request
  useEffect(() => {
    if (permissionCheckStartedRef.current) return;
    permissionCheckStartedRef.current = true;
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    navigator.permissions.query({
      name: 'geolocation'
    }).then(permissionStatus => {
      if (permissionStatus.state === 'granted') {
        setLocationPermission('granted');
      } else if (permissionStatus.state === 'denied') {
        // Check if user previously dismissed the modal
        const wasDismissed = typeof window !== 'undefined' && localStorage.getItem('locationModalDismissed') === 'true';
        setLocationPermission(wasDismissed ? 'dismissed' : 'denied');
      }
      permissionStatus.onchange = () => {
        if (permissionStatus.state === 'denied') {
          // Check localStorage on permission change too
          const wasDismissed = typeof window !== 'undefined' && localStorage.getItem('locationModalDismissed') === 'true';
          setLocationPermission(wasDismissed ? 'dismissed' : 'denied');
        } else {
          setLocationPermission(permissionStatus.state);
        }
      };
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set user location
  const setUserLocation = location => {
    dispatch({
      type: 'SET_USER_LOCATION',
      payload: location
    });
  };

  // Start watching user location for dynamic updates
  const startWatchingLocation = () => {
    if (!navigator.geolocation) return;
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = navigator.geolocation.watchPosition(position => {
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
    }, () => {}, {
      enableHighAccuracy: false,
      timeout: 30000,
      maximumAge: 30000
    });
  };

  // Stop watching location
  const stopWatchingLocation = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  };

  // Request user location with fallback strategy for Android compatibility
  const requestLocation = async () => {
    if (!navigator.geolocation) {
      setLocationPermission('denied');
      return false;
    }
    setLocationPermission('prompt');
    const tryGetPosition = options => new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(position => resolve({
        success: true,
        position
      }), error => resolve({
        success: false,
        error
      }), options);
    });

    // Try high accuracy first, fall back to coarse on timeout/unavailable
    let result = await tryGetPosition({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
    if (!result.success && (result.error.code === 2 || result.error.code === 3)) {
      result = await tryGetPosition({
        enableHighAccuracy: false,
        timeout: 20000,
        maximumAge: 60000
      });
    }
    if (result.success) {
      const location = {
        lat: result.position.coords.latitude,
        lng: result.position.coords.longitude,
        accuracy: result.position.coords.accuracy
      };
      setUserLocation(location);
      setLocationPermission('granted');

      // Check if user is within KC region for UX decisions (e.g., out-of-region messaging).
      // Do not auto-pan on initial location grant; keep the map at default center.
      const isInRegion = isWithinKCRegion(location.lat, location.lng);
      startWatchingLocation();
      return {
        success: true,
        inRegion: isInRegion
      };
    }
    if (result.error.code === 1) {
      // Check if user previously dismissed the modal (persisted in localStorage)
      const wasDismissed = typeof window !== 'undefined' && localStorage.getItem('locationModalDismissed') === 'true';

      // Only show modal if it wasn't previously dismissed
      setLocationPermission(wasDismissed ? 'dismissed' : 'denied');
    } else {
      setLocationPermission('prompt');
    }
    return false;
  };

  // Center map on user location (for re-center button)
  // Returns true if centered, false if user is out of region
  const centerOnUserLocation = () => {
    if (state.userLocation) {
      const isInRegion = isWithinKCRegion(state.userLocation.lat, state.userLocation.lng);
      if (isInRegion) {
        setMapCenter?.(state.userLocation);
        setMapZoom?.(15);
        return true;
      }
      return false;
    }
    return false;
  };

  // Disable location tracking (toggle off)
  const disableLocation = () => {
    stopWatchingLocation();
    setUserLocation(null);
    setLocationPermission('prompt');
  };

  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      stopWatchingLocation();
    };
  }, []);

  // Auto-start watching if permission was previously granted
  useEffect(() => {
    if (state.locationPermission === 'granted' && !watchIdRef.current) {
      startWatchingLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.locationPermission]);
  return {
    userLocation: state.userLocation,
    locationPermission: state.locationPermission,
    setUserLocation,
    setLocationPermission,
    requestLocation,
    centerOnUserLocation,
    disableLocation,
    startWatchingLocation,
    stopWatchingLocation,
    clearDismissal
  };
}